import {computed, inject} from '@angular/core';
import {patchState, signalStore, withComputed, withMethods, withState} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {catchError, of, pipe, switchMap, tap} from 'rxjs';
import {AdminCategoryService} from '../services/admin-category.service';
import {extractErrorMessage} from '@core/utils/error-handler.util';

export interface Category {
  id: number;
  restaurantId: number;
  restaurantName?: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  displayOrder: number;
  active: boolean;
  itemsCount?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface RestaurantOption {
  id: number;
  name: string;
}

interface AdminCategoryState {
  categories: Category[];
  restaurants: RestaurantOption[];
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: AdminCategoryState = {
  categories: [],
  restaurants: [],
  loading: false,
  saving: false,
  error: null
};

export const AdminCategoryStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),

  withComputed((state) => ({
    activeCategories: computed(() =>
      state.categories().filter(c => c.active)
    ),

    categoriesByRestaurant: computed(() => {
      const grouped = new Map<number, Category[]>();
      state.categories().forEach(cat => {
        const existing = grouped.get(cat.restaurantId) || [];
        grouped.set(cat.restaurantId, [...existing, cat]);
      });
      return grouped;
    }),

    totalCategories: computed(() => state.categories().length)
  })),

  withMethods((store, categoryService = inject(AdminCategoryService)) => ({
    loadCategories: rxMethod<void>(
      pipe(
        tap(() => patchState(store, {loading: true, error: null})),
        switchMap(() =>
          categoryService.getCategories().pipe(
            tap((categories) => {
              patchState(store, {
                categories: categories || [],
                loading: false
              });
            }),
            catchError((error) => {
              patchState(store, {
                loading: false,
                error: extractErrorMessage(error, 'Erreur lors du chargement des catégories'),
                categories: getDemoCategories()
              });
              return of(null);
            })
          )
        )
      )
    ),

    loadRestaurants: rxMethod<void>(
      pipe(
        switchMap(() =>
          categoryService.getRestaurants().pipe(
            tap((restaurants) => {
              patchState(store, {restaurants: restaurants || []});
            }),
            catchError(() => {
              patchState(store, {
                restaurants: [
                  {id: 1, name: 'Le Gourmet Parisien'},
                  {id: 2, name: 'Pizza Napoli'},
                  {id: 3, name: 'Sushi Master'}
                ]
              });
              return of(null);
            })
          )
        )
      )
    ),

    createCategory: rxMethod<Partial<Category>>(
      pipe(
        tap(() => patchState(store, {saving: true})),
        switchMap((category) =>
          categoryService.createCategory(category).pipe(
            tap((newCategory) => {
              const restaurant = store.restaurants().find(r => r.id === Number(category.restaurantId));
              const categories = [...store.categories(), {
                ...newCategory,
                id: newCategory.id || Date.now(),
                restaurantName: restaurant?.name,
                itemsCount: 0,
                createdAt: new Date(),
                updatedAt: new Date()
              }];
              patchState(store, {categories, saving: false});
            }),
            catchError((error) => {
              patchState(store, {
                saving: false,
                error: extractErrorMessage(error, 'Erreur lors de la création')
              });
              return of(null);
            })
          )
        )
      )
    ),

    updateCategory: rxMethod<Category>(
      pipe(
        tap(() => patchState(store, {saving: true})),
        switchMap((category) =>
          categoryService.updateCategory(category.id, category).pipe(
            tap(() => {
              const restaurant = store.restaurants().find(r => r.id === Number(category.restaurantId));
              const categories = store.categories().map(c =>
                c.id === category.id
                  ? {...category, restaurantName: restaurant?.name, updatedAt: new Date()}
                  : c
              );
              patchState(store, {categories, saving: false});
            }),
            catchError((error) => {
              patchState(store, {
                saving: false,
                error: extractErrorMessage(error, 'Erreur lors de la mise à jour')
              });
              return of(null);
            })
          )
        )
      )
    ),

    deleteCategory: rxMethod<number>(
      pipe(
        switchMap((id) =>
          categoryService.deleteCategory(id).pipe(
            tap(() => {
              const categories = store.categories().filter(c => c.id !== id);
              patchState(store, {categories});
            }),
            catchError((error) => {
              patchState(store, {
                error: extractErrorMessage(error, 'Erreur lors de la suppression')
              });
              return of(null);
            })
          )
        )
      )
    ),

    clearError(): void {
      patchState(store, {error: null});
    }
  }))
);

function getDemoCategories(): Category[] {
  return [
    {
      id: 1,
      restaurantId: 1,
      restaurantName: 'Le Gourmet Parisien',
      name: 'Entrées',
      description: 'Nos entrées raffinées pour bien commencer votre repas',
      icon: 'fas fa-leaf',
      color: '#10b981',
      displayOrder: 1,
      active: true,
      itemsCount: 12,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 2,
      restaurantId: 1,
      restaurantName: 'Le Gourmet Parisien',
      name: 'Plats principaux',
      description: 'Nos plats signatures préparés avec soin',
      icon: 'fas fa-utensils',
      color: '#3b82f6',
      displayOrder: 2,
      active: true,
      itemsCount: 18,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 3,
      restaurantId: 1,
      restaurantName: 'Le Gourmet Parisien',
      name: 'Desserts',
      description: 'Finissez en beauté avec nos desserts maison',
      icon: 'fas fa-ice-cream',
      color: '#ec4899',
      displayOrder: 3,
      active: true,
      itemsCount: 8,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 4,
      restaurantId: 2,
      restaurantName: 'Pizza Napoli',
      name: 'Pizzas Classiques',
      description: 'Nos recettes traditionnelles italiennes',
      icon: 'fas fa-pizza-slice',
      color: '#f59e0b',
      displayOrder: 1,
      active: true,
      itemsCount: 15,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 5,
      restaurantId: 2,
      restaurantName: 'Pizza Napoli',
      name: 'Boissons',
      description: 'Sodas, vins et bières',
      icon: 'fas fa-wine-glass-alt',
      color: '#8b5cf6',
      displayOrder: 2,
      active: true,
      itemsCount: 10,
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];
}

